import { useState, useRef, useEffect, useCallback } from 'react';
import { Upload, Image as ImageIcon } from 'lucide-react';

/**
 * Validate image file
 * @param {File} file - File to validate
 * @returns {string|null} Error message or null if valid
 */
const validateImage = (file) => {
  const validTypes = ['image/png', 'image/jpeg', 'image/jpg'];
  if (!validTypes.includes(file.type)) {
    return 'Nur PNG und JPG Dateien erlaubt';
  }
  if (file.size > 10 * 1024 * 1024) {
    return 'Datei zu groß (max 10MB)';
  }
  return null;
};

/**
 * Pasted images arrive as blobs that may carry no filename (browser-dependent).
 * Wrap them in a File with a sensible default name so the preview and file info
 * render like an uploaded file. Files that already have a name pass through.
 * @param {File} blob - Blob/File from clipboard
 * @returns {File}
 */
const normalizePastedFile = (blob) => {
  if (blob.name) {
    return blob;
  }
  const extension = blob.type === 'image/png' ? 'png' : 'jpg';
  return new File([blob], `Eingefügtes Bild.${extension}`, { type: blob.type });
};

/**
 * ImageUploader Component
 *
 * Drag-and-drop file upload for background images with preview and validation.
 * Also supports pasting an image from the clipboard (Ctrl+V), e.g. a screenshot
 * from Google Maps (#765).
 * Design based on: docs/design/screenshots/05_Dialog_Hintergrundbild_Import_*.png
 *
 * @param {Object} props
 * @param {File|null} props.file - Current uploaded file
 * @param {string|null} props.dataUrl - Base64 data URL for preview
 * @param {Function} props.onFileSelect - Callback when file is selected (file, dataUrl, error)
 * @param {Function} props.onFileRemove - Callback when file is removed
 * @param {string|null} props.error - Error message to display
 */
const ImageUploader = ({ file, dataUrl, onFileSelect, onFileRemove, error }) => {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  /**
   * Process selected file
   * @param {File} selectedFile - File to process
   */
  const processFile = useCallback((selectedFile) => {
    const validationError = validateImage(selectedFile);
    if (validationError) {
      onFileSelect(null, null, validationError);
      return;
    }

    // Generate preview using FileReader
    const reader = new FileReader();
    reader.onload = (e) => {
      onFileSelect(selectedFile, e.target.result, null);
    };
    reader.onerror = () => {
      onFileSelect(null, null, 'Fehler beim Laden der Datei');
    };
    reader.readAsDataURL(selectedFile);
  }, [onFileSelect]);

  /**
   * Handle image paste from clipboard (Ctrl+V).
   * Only active while no image is loaded yet, so it never clobbers an existing
   * selection or interferes with pasting into the calibration inputs below.
   */
  useEffect(() => {
    if (file) {
      return undefined;
    }

    const handlePaste = (e) => {
      const items = e.clipboardData?.items;
      if (!items) {
        return;
      }
      for (const item of items) {
        if (item.kind === 'file' && item.type.startsWith('image/')) {
          const blob = item.getAsFile();
          if (blob) {
            e.preventDefault();
            processFile(normalizePastedFile(blob));
            return;
          }
        }
      }
    };

    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [file, processFile]);

  /**
   * Handle file selection from input
   */
  const handleFileInput = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      processFile(selectedFile);
    }
  };

  /**
   * Handle drag enter
   */
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  /**
   * Handle drag leave
   */
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set isDragging to false if we're leaving the drop zone itself
    if (e.currentTarget === e.target) {
      setIsDragging(false);
    }
  };

  /**
   * Handle drag over
   */
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  /**
   * Handle drop
   */
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) {
      processFile(droppedFile);
    }
  };

  /**
   * Handle click to browse
   */
  const handleClick = () => {
    fileInputRef.current?.click();
  };

  /**
   * Handle remove image
   */
  const handleRemove = (e) => {
    e.stopPropagation();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onFileRemove();
  };

  return (
    <div className="space-y-4">
      {/* Upload Area - Design: 1152×650px dashed border, centered content */}
      {!file && (
        <div
          role="button"
          tabIndex={0}
          onClick={handleClick}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleClick();
            }
          }}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          aria-label="Hintergrundbild hochladen — klicken oder per Drag-and-Drop ablegen"
          className={`
            relative border-2 border-dashed rounded-lg
            cursor-pointer transition-colors
            flex items-center justify-center
            ${isDragging
              ? 'border-emerald-500 bg-emerald-50'
              : 'border-gray-300 bg-gray-50 hover:border-gray-400'
            }
          `}
          style={{ minHeight: '400px' }}
          data-testid="upload-area"
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/jpg"
            onChange={handleFileInput}
            className="hidden"
            data-testid="file-input"
          />

          <div className="flex flex-col items-center gap-4 text-center px-4">
            {/* Upload Icon */}
            <div className={`
              p-4 rounded-lg transition-colors
              ${isDragging ? 'bg-white' : 'bg-white'}
            `}>
              <Upload
                size={48}
                className={isDragging ? 'text-emerald-600' : 'text-gray-400'}
                strokeWidth={1.5}
              />
            </div>

            {/* Main Text */}
            <div>
              <p className="text-lg font-medium text-gray-900">
                {isDragging ? 'Bild hier ablegen' : 'Drag & Drop'}
              </p>
              <p className="text-sm text-gray-600 mt-1">
                oder klicken zum Durchsuchen
              </p>
              <p className="text-sm text-gray-600 mt-1">
                oder Bild einfügen (Strg+V)
              </p>
            </div>

            {/* Supported Formats */}
            <p className="text-xs text-gray-500">
              unterstützt: JPG, PNG
            </p>
          </div>
        </div>
      )}

      {/* Preview Area - Design: Image with dimensions label, "Bild ändern" button */}
      {file && dataUrl && (
        <div className="space-y-3">
          {/* Image Preview Container */}
          <div className="relative border-2 border-dashed border-gray-300 rounded-lg overflow-hidden bg-white">
            {/* --preview-min-h collapses to a slim thumbnail on touch + short
                viewports (#486); falls back to the full 400px box on desktop. */}
            <div
              className="wizard-image-preview-box relative flex items-center justify-center"
              style={{ minHeight: 'var(--preview-min-h, 400px)' }}
            >
              {/* Image Preview */}
              <img
                src={dataUrl}
                alt="Vorschau"
                className="wizard-image-preview-img max-w-full object-contain"
                style={{ maxHeight: 'var(--preview-max-h, 400px)' }}
                data-testid="image-preview"
              />
            </div>
          </div>

          {/* Image Info and Change Button */}
          <div className="flex items-center justify-between">
            {/* Left: File Info */}
            <div className="flex items-center gap-2 text-sm text-gray-700">
              <ImageIcon size={16} className="text-gray-500 flex-shrink-0" />
              <span className="font-medium truncate max-w-xs" title={file.name}>
                {file.name}
              </span>
              <span className="text-gray-400">
                ({(file.size / (1024 * 1024)).toFixed(2)} MB)
              </span>
            </div>

            {/* Right: Change Image Button */}
            <button
              onClick={handleRemove}
              className="px-4 py-1.5 text-sm text-gray-700 border border-gray-300 rounded-md
                         hover:bg-gray-50 transition-colors"
              data-testid="change-image-button"
            >
              Bild ändern
            </button>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-700" data-testid="error-message">
            {error}
          </p>
        </div>
      )}
    </div>
  );
};

export default ImageUploader;
